import React, { useEffect } from 'react';

import { FormikValues } from 'formik';

import {
  adminActions,
  adminApproveRefund,
  adminRejectRefund,
  ConfirmationTypes,
  getPendingRefunds,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';
import strings from '../../../localization';
import { resetDialog, setDialog } from '../../../reducers/dialogReducer';
import { useAppDispatch } from '../../useAppDispatch';
import Validator from '../../../components/Admin/PendingRequestsList/validator';
import { useAppSelector } from '../../useAppSelector';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import { resetDrawer } from '../../../actions/drawerActions';
import DeactivateDialog from '../../../components/Admin/PharmacistList/DeactivateDialog';
import { DeactivateModalIcon } from '../../../helpers/Constants';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';

const { approveRefund: approveRefundAction, rejectRefund: rejectRefundAction } = adminActions;

const usePendingRefundActions = () => {
  const dispatch = useAppDispatch();

  const approveSuccess = useAppSelector((state) =>
    successSelector([approveRefundAction], state));

  const rejectionSuccess = useAppSelector((state) =>
    successSelector([rejectRefundAction], state));

  const approveAction = (refundRequestId: string) =>
    () => {
      dispatch(adminApproveRefund({ refundRequestId }));
    };

  const rejectAction = (values: FormikValues) => {
    const { id, reason } = values ?? {};
    dispatch(adminRejectRefund({ refundRequestId: id, notes: reason }));
  };

  const approveForm = (refundRequestId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <ConfirmationModal
              type={ConfirmationTypes.approveRefund}
              confirmAction={approveAction(refundRequestId)}
            />
          ),
          showCloseButton: false,
        }),
      );
    };

  const rejectRefundForm = (refundRequestId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <DeactivateDialog
              validator={Validator()}
              heading={strings.reject}
              id={refundRequestId}
              iconType={DeactivateModalIcon.warning}
              onSubmit={rejectAction}
              confirmationMessage={strings.areYouSureRejectRefund}
            />
          ),
          heading: {
            title: '',
          },
          showCloseButton: true,
        }),
      );
    };

  const resetActions = () => {
    dispatch(resetStatus([approveRefundAction, rejectRefundAction]));
    dispatch(resetDrawer());
    dispatch(getPendingRefunds({ page: 1 }));
    dispatch(resetDialog());
  };

  useEffect(() => {
    if (approveSuccess || rejectionSuccess) {
      showSuccess(
        dispatch,
        approveSuccess ? strings.approvedSuccess : strings.rejectedSuccess,
      );
      resetActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approveSuccess, rejectionSuccess]);

  return { approveAction: approveForm, rejectRefundForm };
};

export default usePendingRefundActions;
